import { switchMap, tap } from 'rxjs/operators';
import { DragulaService } from 'ng2-dragula';
import { Observable ,  Subscription, of } from 'rxjs';

import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';

import {PieceActeAttaque, PieceComplementaire, PieceComplementaireOrigineType,
    PieceJointType, PieceReclamationPrealable, PieceRequete} from '../../../entities/piece-jointe';
import { ScrollToService } from '../../../shared';
import { FileUploaderComponent } from '../../../shared/components/file-uploader/file-uploader.component';
import { FileUploaderModel } from '../../../shared/components/file-uploader/file-uploader.model';
import { SectionState } from '../../../shared/components/section/section.model';
import { MAX_FILE_EXCEEDED_ERROR_CODE } from '../../../shared/constants/filesConstraints.constants';
import { ErrorService } from '../../../shared/services/error.service';
import { ParametersService } from '../../../shared/services/parameters.service';
import { DepotRequeteState } from '../../depot-requete-state.model';
import { DraftDepotRequeteService } from '../../draft-depot-requete.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ContentieuxSocial } from '../../../entities/contentieux-social';
import { DraftRequete } from '../../../entities/draft-requete/draft-requete.model';

@Component({
    templateUrl: './justificatifs.component.html',
    styleUrls: ['justificatifs.component.scss']
})
export class JustificatifsComponent implements OnDestroy, AfterViewChecked, OnInit, AfterViewInit {
    depotRequeteState: DepotRequeteState;

    PieceJointTypeEnum = PieceJointType;

    maxNumberJointsPiecesExceptRequete = 98; // Nombre maximum de pièces jointes pour une requete est fixé à 99
    showLengthWarningForFileName = false;
    maxFileNameWithoutExtensionLength: number;
    maxFileSize: number;
    maxActeAttaquesForMerge: number;
    maxReclamationPrealablesForMerge: number;
    actualFileNameLength = 0;

    sections: { [key: string]: SectionState } = {
        obligatoires: { index: 1, isToggleable: false, isHelpOpen: true },
        complementaires: { index: 2, isToggleable: false, isHelpOpen: true },
        postales: { index: 3, isToggleable: false, isHelpOpen: true }
    };

    // For validation only
    _justificatifRenommage = false;

    private parameterSubscription: Subscription;
    private dropSubscription: Subscription;
    private dragSubscription: Subscription;
    private scrollToError = false;
    public requeteAutoSaveSuccess = false;
    public autoSaveError = false;
    public saveDraftSuccess = false;
    public saveDraftError = false;
    private autoSaveTime: any;

    @Output() previousStep: EventEmitter<any> = new EventEmitter();
    @Output() nextStep: EventEmitter<any> = new EventEmitter();
    @Output() hideAlertDraft: EventEmitter<any> = new EventEmitter();
    @Output() showErrorAlertDraft: EventEmitter<any> = new EventEmitter();
    @Output() showSuccessAlertDraft: EventEmitter<any> = new EventEmitter();
    @Output() showAutoSaveSuccessAlertDraft: EventEmitter<any> = new EventEmitter();

    @ViewChild(NgForm) form: NgForm;
    @ViewChild('pieceAdder') pieceAdder: FileUploaderComponent;

    // --- drag n drop keyboard accessible
    public selectedItem = null;

    constructor(
        private dragulaService: DragulaService,
        private parametersService: ParametersService,
        private errorService: ErrorService,
        private scrollToService: ScrollToService,
        private draftDepotRequeteService: DraftDepotRequeteService,
        private liveAnnouncer: LiveAnnouncer,
        private cdRef: ChangeDetectorRef
    ) {
        this.depotRequeteState = this.draftDepotRequeteService.depotRequeteState;
        if (this.depotRequeteState.isStepRequeteAutoSaveSucceed && !this.depotRequeteState.isStepJustificatifsAutoSaveSucceed) {
            this.requeteAutoSaveSuccess = true;
            this.autoSaveTime = this.draftDepotRequeteService.getAutoSaveTime();
            this.draftDepotRequeteService.sendData(this.autoSaveTime);
        }
        if (this.dragulaService.find('piecesComplementairesBag') === undefined) {
            dragulaService.setOptions('piecesComplementairesBag', {});
        }
        this.parameterSubscription = this.parametersService.getParameters().subscribe(parameters => {
            this.maxFileNameWithoutExtensionLength = parameters.maxFileNameWithoutExtensionLength;
            this.maxFileSize = parameters.maxFileSize;
            this.maxActeAttaquesForMerge = parameters.maxActeAttaquesForMerge;
            this.maxReclamationPrealablesForMerge = parameters.maxActeAttaquesForMerge;
        });

        this.dropSubscription = this.dragulaService.out.subscribe(() => {
            this.liveAnnouncer.announce('élément relaché');
        });

        this.dragSubscription = this.dragulaService.drag.subscribe(() => {
          this.liveAnnouncer.announce('élément selectionné');
        });
    }

    ngOnInit(): void {
        // refresh pieces complémentaires => permet de continuer d utiliser dragula sans rafraichir la page
        this.depotRequeteState.piecesComplementaires = this.depotRequeteState.piecesComplementaires.map((x) => x);
        this.hideAlertDraft.emit();
        if (this.requeteAutoSaveSuccess) {
            this.showAutoSaveSuccessAlertDraft.emit();
        }
    }

    ngAfterViewInit() {
        this.draftDepotRequeteService.refreshRequete().pipe(
            tap(draftRequete => this.updateDepotRequeteState(draftRequete)),
            switchMap(draftRequete => draftRequete.contentieuxSocialId ?
                this.draftDepotRequeteService.getContentieuxSocial(draftRequete.contentieuxSocialId) :
                of(new ContentieuxSocial())),
                tap(cs => this.depotRequeteState.requeteCS = cs)
            ).subscribe()

        document.getElementById('logo-link').focus();
    }

    private updateDepotRequeteState(draftRequete: DraftRequete): void {
        if (draftRequete.typeRequeteId == null) {
            this.depotRequeteState.urgenceRequete = undefined!;
        } else {
            this.depotRequeteState.urgenceRequete = this.depotRequeteState.typeJuridiction.urgencesRequete.find(urgenceRequete =>
                urgenceRequete.id === draftRequete.typeRequeteId)!;
        }
        this.depotRequeteState.concernContentieuxSocial = draftRequete.concernContentieuxSocial;
        this.depotRequeteState.formActivated = draftRequete.formActivated;
        this.draftDepotRequeteService.refreshPiecesFromDraft(draftRequete, this.depotRequeteState);
    }

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
        if (this.scrollToError) {
            this.scrollToService.scrollToFirstError();
            this.scrollToError = false;
        }
    }

    next(): Observable<void> {
        this.hideAlertDraft.emit();
        if (this.form.valid && !this.isActeAttaquesMaxSizeExceeded) {
            return this.saveJustificatifsAndMergeActeAttaquesIfNeeded();
        } else {
            this.errorService.markAllControlsAsTouched(this.form);
            this.scrollToError = true;
        }
    }

    previous(): void {
        this.depotRequeteState.isStepJustificatifsAutoSaveSucceed = false;
        this.previousStep.emit();
    }

    onRequeteChange(file: FileUploaderModel) {
        if (this.depotRequeteState.requete) {
            this.depotRequeteState.requete.fileModel = file;
        } else {
            this.depotRequeteState.requete = new PieceRequete(file);
        }
    }

    onPieceComplementaireAdd(file: FileUploaderModel) {
        if (file) {
            this.depotRequeteState.piecesComplementaires.push(
                new PieceComplementaire(file, PieceJointType.PIECE_JOINTE_REQUETE, PieceComplementaireOrigineType.DEFAULT)
            );
            this.pieceAdder.deleteFile();
        }
    }

    public disableDeletion(piece: PieceComplementaire): boolean {
        return piece.complementaryFileOriginType !== PieceComplementaireOrigineType.DEFAULT;
    }

    onPieceComplementaireDelete(index: number, file: FileUploaderModel) {
        if (!file) {
            this.depotRequeteState.piecesComplementaires.splice(index, 1);
        }
    }

    moveUp(index: number) {
        if (index > 0) {
            this.swapPiecesComplementaire(index - 1, index);
        }
    }

    moveDown(index: number) {
        if (index < this.depotRequeteState.piecesComplementaires.length - 1) {
            this.swapPiecesComplementaire(index + 1, index);
        }
    }

    // @ts-ignore
    trackByFileApiId(index: number, item: PieceComplementaire) {
        return item.fileModel.fileApiId;
    }

    private swapPiecesComplementaire(piece1Index: number, piece2Index: number) {
        const piece1 = this.depotRequeteState.piecesComplementaires[piece1Index];
        const piece2 = this.depotRequeteState.piecesComplementaires[piece2Index];
        this.depotRequeteState.piecesComplementaires[piece2Index] = piece1;
        this.depotRequeteState.piecesComplementaires[piece1Index] = piece2;
    }

    ngOnDestroy() {
        this.parameterSubscription.unsubscribe();
        this.dragSubscription.unsubscribe();
        this.dropSubscription.unsubscribe();
    }

    checkLengthForFileName(value: string) {
        if (value) {
            this.actualFileNameLength = value.length;
            this.showLengthWarningForFileName = value.length > this.maxFileNameWithoutExtensionLength;
        }
    }

    resetGeneratedActeAttaque() {
        this.depotRequeteState.acteAttaque = new PieceActeAttaque(null);
    }

    resetGeneratedReclamationPrealable() {
        this.depotRequeteState.reclamationPrealable = new PieceReclamationPrealable(null);
    }

    get isActeAttaquesMaxSizeExceeded(): boolean {
        return (
            this.depotRequeteState.acteAttaquesAFusioner.reduce((acc, aa) => acc + aa.fileModel.fileSize, 0) >
            this.maxFileSize
        );
    }

    get isReclamationPrealablesMaxSizeExceeded(): boolean {
        return (
            this.depotRequeteState.reclamationPrealablesAFusioner.reduce((acc, aa) => acc + aa.fileModel.fileSize, 0) >
            this.maxFileSize
        );
    }

    private saveJustificatifsAndMergeActeAttaquesIfNeeded(): Observable<void> {
        return this.saveJustificatifs().pipe(tap(
            model => {
                console.log('saveJustificatifsAndMergeActeAttaquesIfNeeded model | ', model);
                this.depotRequeteState.isStepJustificatifsAutoSaveSucceed = true;
                if (Array.isArray(model) && model.length === 1) {
                    if (model[0].fileItem.name === 'Acte attaqué') {
                        this.depotRequeteState.acteAttaque = new PieceActeAttaque(model[0]);
                    } else {
                        this.depotRequeteState.reclamationPrealable = new PieceReclamationPrealable(model[0]);
                    }
                } else if (Array.isArray(model) && model.length === 2) {
                    this.depotRequeteState.acteAttaque =
                    new PieceActeAttaque(model.find(m => m.fileItem.name === 'Acte attaqué'));
                    this.depotRequeteState.reclamationPrealable =
                    new PieceReclamationPrealable(model.find(m => m.fileItem.name === 'Réclamation préalable'));
                }
                this.nextStep.emit();
            },
            error => {
                if (error.error.code === MAX_FILE_EXCEEDED_ERROR_CODE) {
                    this.form.controls['acteAttaques'].setErrors({ invalid: true, 'max-file-length-exceeded': true });
                    this.errorService.markAllControlsAsTouched(this.form);
                    this.scrollToError = true;
                } else {
                    this.autoSaveError = true;
                    this.draftDepotRequeteService.displayOrNotErrorAlertDraft(error, this.showErrorAlertDraft);
                }
            }
        ));
    }

    saveDraft(): Observable<void> {
        this.hideAlertDraft.emit();

        return this.saveJustificatifs().pipe(tap(
            () => {
                this.saveDraftSuccess = true;
                this.requeteAutoSaveSuccess = false;
                this.showSuccessAlertDraft.emit();
            },
            error => {
                this.saveDraftError = true;
                this.requeteAutoSaveSuccess = false;
                this.draftDepotRequeteService.displayOrNotErrorAlertDraft(error, this.showErrorAlertDraft);
            }
        ));
    }

    saveDraftAgain(): void {
        this.saveJustificatifs().subscribe(
            () => {
                this.saveDraftSuccess = true;
                this.requeteAutoSaveSuccess = false;
                this.showSuccessAlertDraft.emit();
            },
            error => {
                this.saveDraftError = true;
                this.requeteAutoSaveSuccess = false;
                this.draftDepotRequeteService.displayOrNotErrorAlertDraft(error, this.showErrorAlertDraft);
            }
        );
    }

    close() {
        this.autoSaveError = false;
    }

    saveJustificatifs(): Observable<any> {
        return this.draftDepotRequeteService.saveJustificatifsAndMergeActeAttaquesIfNeeded();
    }

    get uploadUrl(): string {
        return this.draftDepotRequeteService.buildUploadUrl();
    }

    // --- drag n drop keyboard accessible
    onMoveup(data: any) {
        console.log('onMoveup', data);
        this.moveUp(data.i);
    }

    onMovedown(data: any) {
        console.log('onMovedown', data);
        this.moveDown(data.i);
    }

    onSelect(data: any) {
        console.log('onSelect', data);
        this.selectedItem = data.piece.fileModel.fileApiId;
    }

    onEscape(data: any) {
        console.log('onEscape', data);
        this.selectedItem = null;
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/depot-requete/steps/justificatifs/justificatifs.component.ts